import React, { useState } from 'react';
import { Lock, Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../lib/constants';

const ResetPasswordForm = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const validatePassword = () => {
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return false;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validatePassword()) return;

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) throw error;

      setSuccess(true);
      toast.success('Password updated successfully!');
      
      // Redirect after 2 seconds
      setTimeout(() => {
        navigate(ROUTES.LOGIN);
      }, 2000);
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="w-full max-w-md mx-auto px-4 sm:px-6 md:px-8">
        <div className="backdrop-blur-lg bg-white/40 border border-cream-300/50 rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10">
          <div className="text-center">
            {/* Success Icon */}
            <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mb-4 sm:mb-6 shadow-lg animate-fade-in">
              <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </div>
            
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-brown-900 mb-2 sm:mb-3">
              Password Reset!
            </h2>
            
            <p className="text-sm sm:text-base text-brown-700 mb-6 sm:mb-8">
              Your password has been updated successfully. Redirecting to login...
            </p>
            
            <div className="flex justify-center">
              <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 border-brown-200 border-t-brown-600 rounded-full animate-spin"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto px-4 sm:px-6 md:px-8">
      <div className="backdrop-blur-lg bg-white/40 border border-cream-300/50 rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 md:p-10">
        
        {/* Logo/Brand */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 md:w-18 md:h-18 bg-gradient-to-br from-brown-500 to-brown-700 rounded-full mb-3 sm:mb-4 shadow-lg">
            <Lock className="w-7 h-7 sm:w-8 sm:h-8 md:w-9 md:h-9 text-cream-50" />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-brown-900 mb-2 sm:mb-3">
            Set New Password
          </h2>
          <p className="text-sm sm:text-base text-brown-600">
            Create a strong password for your account
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 md:space-y-6">
          {/* New Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm sm:text-base font-medium text-brown-800 mb-2">
              New Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-brown-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 6 characters"
                className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-2.5 sm:py-3 md:py-3.5 
                  bg-white/60 backdrop-blur-sm border-2 border-cream-300 rounded-lg sm:rounded-xl 
                  focus:ring-2 focus:ring-brown-500 focus:border-brown-500 
                  outline-none transition-all text-sm sm:text-base
                  placeholder:text-brown-300 text-brown-900"
                disabled={loading}
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-brown-400 hover:text-brown-600 transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
              </button>
            </div>
            <p className="text-xs sm:text-sm text-brown-600 mt-1 sm:mt-1.5">
              Must be at least 6 characters
            </p>
          </div>

          {/* Confirm Password Field */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm sm:text-base font-medium text-brown-800 mb-2">
              Confirm Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-brown-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter password"
                className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-2.5 sm:py-3 md:py-3.5 
                  bg-white/60 backdrop-blur-sm border-2 border-cream-300 rounded-lg sm:rounded-xl 
                  focus:ring-2 focus:ring-brown-500 focus:border-brown-500 
                  outline-none transition-all text-sm sm:text-base
                  placeholder:text-brown-300 text-brown-900"
                disabled={loading}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-brown-400 hover:text-brown-600 transition-colors"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-brown-600 to-brown-700 hover:from-brown-700 hover:to-brown-800 
              text-white font-medium py-2.5 sm:py-3 md:py-3.5 px-4 rounded-lg sm:rounded-xl 
              transition-all duration-200 flex items-center justify-center 
              disabled:opacity-50 disabled:cursor-not-allowed shadow-lg
              text-sm sm:text-base md:text-lg"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2 animate-spin" />
                <span>Resetting...</span>
              </>
            ) : (
              'Reset Password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordForm;